import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import CardCanvas from './CardCanvas';
import { MAIN_TYPES, SUB_TYPES } from '../constants';
import { FaImage, FaPlus, FaMinus, FaLockOpen, FaDownload, FaSliders } from "react-icons/fa6";
import { FaArrowsAlt } from "react-icons/fa";
import { GiCardPick } from "react-icons/gi";
import { MdOutlineScreenshot } from "react-icons/md";
import { BiSolidFileExport, BiSolidFileImport } from "react-icons/bi";
import { useUser } from '../../../UserContext';

const ART_ADJUSTMENTS = [
  { key: 'brightness', label: 'Brightness', min: 0, max: 200, defaultValue: 100, suffix: '%' },
  { key: 'contrast', label: 'Contrast', min: 0, max: 200, defaultValue: 100, suffix: '%' },
  { key: 'saturation', label: 'Saturation', min: 0, max: 200, defaultValue: 100, suffix: '%' },
  { key: 'hue', label: 'Hue Shift', min: 0, max: 360, defaultValue: 0, suffix: '\u00b0' }
];

export default function PreviewSection({ cardParams, updateParam, onExport, onImport, onOpenModal, onTextRendered, showToast, cardCanvasRef }) {
  const [mainType, setMainType] = useState('minion');
  const [zoomLevel, setZoomLevel] = useState(100);
  const [zoomConstraints, setZoomConstraints] = useState({ min: 10, max: 300 });
  const [isArtLocked, setIsArtLocked] = useState(false);
  const [showArtAdjustPanel, setShowArtAdjustPanel] = useState(false);
  const [isCopyingToClipboard, setIsCopyingToClipboard] = useState(false);
  const copyInProgressRef = useRef(false);
  const { settings } = useUser();

    const canvasRef = cardCanvasRef;
  
  // Mobile Floating Preview Refs & States
  const [isMini, setIsMini] = useState(false);
  const placeholderRef = useRef(null);
  const layoutRef = useRef(null);
  const miniState = useRef({
    x: 0, y: 0, scale: 1,
    startX: 0, startY: 0, tapStartX: 0, tapStartY: 0,
    initialScale: 1, pinchDist: null,
    isDragging: false, hasMoved: false
  });

  const jsonInputRef = useRef(null); // Reference for hidden file input

  // Add function to store art transform into cardParams
  const handleArtTransform = (transformData) => {
    // We only update state when dragging/zooming STOPS to prevent infinite render lag
    updateParam('Art', { ...cardParams.Art, ...transformData });
  };

  const updateArtAdjustment = (key, value) => {
    updateParam('Art', { ...cardParams.Art, [key]: value });
  };

  const resetArtAdjustments = () => {
    updateParam('Art', {
      ...cardParams.Art,
      brightness: 100,
      contrast: 100,
      saturation: 100,
      hue: 0,
      flipX: false
    });
  };

  useEffect(() => {
    const currentSubType = cardParams?.Type;
    if (!currentSubType) return;

    // Find which main category contains this sub-type
    const foundMainType = Object.keys(SUB_TYPES).find(mainKey => 
      SUB_TYPES[mainKey].some(sub => sub.id === currentSubType)
    );

    // If found, update the mainType state (using functional update to avoid dependency loops)
    if (foundMainType) {
      setMainType(prev => (prev !== foundMainType ? foundMainType : prev));
    }
  }, [cardParams?.Type]);

  const handleMainTypeChange = (typeId) => {
    setMainType(typeId);
    updateParam('Type', SUB_TYPES[typeId][0].id);
  };

  // Callback from CardCanvas when a new image loads
  const handleZoomSetup = useCallback(({ min, current }) => {
    setZoomConstraints({ min: Math.floor(min), max: Math.ceil(min * 3) });
    setZoomLevel(current);
  }, []);

  // --- Scroll Listener for Pop-out ---
useEffect(() => {
  let ticking = false;

  const handleScroll = () => {
    if (window.innerWidth >= 992 || !settings.popoutPreview) { 
      if (isMini) setIsMini(false);
      return; 
    }

    // Only allow this to run once per frame
    if (!ticking) {
      window.requestAnimationFrame(() => {
        if (placeholderRef.current) {
          const rect = placeholderRef.current.getBoundingClientRect();
          if (rect.bottom < 150) {
            if (!isMini) setIsMini(true);
          } else {
            if (isMini) setIsMini(false);
          }
        }
        ticking = false;
      });
      ticking = true;
    }
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
  handleScroll(); // Check on mount
  
  return () => window.removeEventListener('scroll', handleScroll);
}, [isMini, settings.popoutPreview]);

  useEffect(() => {
    if (!isMini && layoutRef.current) {
      layoutRef.current.style.transform = '';
      miniState.current = { ...miniState.current, x: 0, y: 0, scale: 1, pinchDist: null };
    }
  }, [isMini]);

  // --- High-Performance Touch Gesture Logic (From previous step) ---
  const getDistance = (touches) => Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);

  const updateMiniTransform = useCallback(() => {
    if (layoutRef.current) {
      const { x, y, scale } = miniState.current;
      layoutRef.current.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
    }
  }, []);

  useEffect(() => {
    const layoutEl = layoutRef.current;
    if (!layoutEl) return;

    const onTouchStart = (e) => {
      if (!layoutEl.classList.contains('is-mini')) return;
      e.stopPropagation();
      miniState.current.hasMoved = false;

      const state = miniState.current;
      const rect = layoutEl.getBoundingClientRect();

      state.width = rect.width;
      state.height = rect.height;
      state.baseLeft = rect.left - state.x;
      state.baseTop = rect.top - state.y;
      state.viewW = window.innerWidth;
      state.viewH = window.visualViewport ? window.visualViewport.height : window.innerHeight;

      if (e.touches.length === 1) {
        state.isDragging = true;
        state.tapStartX = e.touches[0].clientX;
        state.tapStartY = e.touches[0].clientY;
        state.startX = e.touches[0].clientX - state.x;
        state.startY = e.touches[0].clientY - state.y;
      } else if (e.touches.length === 2) {
        state.isDragging = false;
        state.pinchDist = getDistance(e.touches);
        state.initialScale = state.scale;
      }
    };

    const onTouchMove = (e) => {
      if (!layoutEl.classList.contains('is-mini')) return;
      e.preventDefault();
      e.stopPropagation();

      const state = miniState.current;

      if (e.touches.length === 1 && state.isDragging) {
        if (Math.abs(e.touches[0].clientX - state.tapStartX) > 10 || Math.abs(e.touches[0].clientY - state.tapStartY) > 10) {
          state.hasMoved = true;
        }

        const proposedX = e.touches[0].clientX - state.startX;
        const proposedY = e.touches[0].clientY - state.startY;

        const newLeft = state.baseLeft + proposedX;
        const newTop = state.baseTop + proposedY;
        const safeW = state.width * 0.2;
        const safeH = state.height * 0.2;
        const minLeft = -state.width + safeW;
        const maxLeft = state.viewW - safeW;
        const minTop = -state.height + safeH;
        const maxTop = state.viewH - safeH;

        let clampedX = proposedX, clampedY = proposedY;
        if (newLeft < minLeft) clampedX = minLeft - state.baseLeft;
        else if (newLeft > maxLeft) clampedX = maxLeft - state.baseLeft;
        if (newTop < minTop) clampedY = minTop - state.baseTop;
        else if (newTop > maxTop) clampedY = maxTop - state.baseTop;

        state.x = clampedX;
        state.y = clampedY;
        updateMiniTransform();
      } else if (e.touches.length === 2) {
        state.hasMoved = true;
        const currentDistance = getDistance(e.touches);
        if (state.pinchDist) {
          state.scale = Math.max(0.5, Math.min(3.5, state.initialScale * (currentDistance / state.pinchDist)));
          const newLeft = state.baseLeft + state.x;
          const newTop = state.baseTop + state.y;
          const safeW = (state.width * state.scale) * 0.2;
          const safeH = (state.height * state.scale) * 0.2;
          if (newLeft < -state.width + safeW) state.x = (-state.width + safeW) - state.baseLeft;
          if (newTop < -state.height + safeH) state.y = (-state.height + safeH) - state.baseTop;
          updateMiniTransform();
        }
      }
    };

    const onTouchEnd = (e) => {
      if (!layoutEl.classList.contains('is-mini')) return;
      if (e.touches.length < 2) miniState.current.pinchDist = null;
      if (e.touches.length === 0) miniState.current.isDragging = false;
    };

    layoutEl.addEventListener('touchstart', onTouchStart, { passive: false });
    layoutEl.addEventListener('touchmove', onTouchMove, { passive: false });
    layoutEl.addEventListener('touchend', onTouchEnd);

    return () => {
      layoutEl.removeEventListener('touchstart', onTouchStart);
      layoutEl.removeEventListener('touchmove', onTouchMove);
      layoutEl.removeEventListener('touchend', onTouchEnd);
    };
  }, [updateMiniTransform]);

const handleMiniClick = () => {
    if (miniState.current.hasMoved) return;
    if (isMini && placeholderRef.current) {
      // Cleanest way to center an element on the screen
      placeholderRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    }
  };

  const handleCopyToClipboard = useCallback(async () => {
    const cardCanvas = canvasRef.current;

    if (!cardCanvas?.copyToClipboard || copyInProgressRef.current) return;

    copyInProgressRef.current = true;
    setIsCopyingToClipboard(true);

    const startedAt = Date.now();

    try {
      await cardCanvas.copyToClipboard();
    } catch (error) {
      if (showToast) {
        showToast(error?.message || 'Could not copy the card image. Please try again.');
      }
    } finally {
      // Keep the feedback visible long enough to register, even on fast devices.
      const remainingTime = Math.max(0, 10 - (Date.now() - startedAt));

      if (remainingTime > 0) {
        await new Promise((resolve) => setTimeout(resolve, remainingTime));
      }

      copyInProgressRef.current = false;
      setIsCopyingToClipboard(false);
    }
  }, [canvasRef, showToast]);

  return (
    <div className="preview-sticky">
      {/* Type Selector Grid */}
<div className="type-selector-wrapper mb-2">
  <div className="type-list no-swipe">
    {MAIN_TYPES.map(type => (
      <div key={type.id} className={`type-btn ${mainType === type.id ? 'active' : ''}`} onClick={() => handleMainTypeChange(type.id)}>
        <img src={`/images/mobile/${type.icon}`} className="type-icon" alt={type.label} />
        <span className="type-name">{type.label}</span>
      </div>
    ))}

    {/* Custom Card Type Selector Button */}
    <div 
      className={`type-btn ${!MAIN_TYPES.some(t => t.id === mainType) ? 'active' : ''}`} 
      onClick={() => onOpenModal('customCardType')}
    >
      {/* <img src="/images/mobile/custom_type.webp" className="type-icon" alt="Custom Type" /> */}
      <span className="type-name">Custom</span>
    </div>
  </div>
</div>

<div className="submenu-container no-swipe">
  {SUB_TYPES[mainType]?.some(sub => sub.id === cardParams.Type) && 
    SUB_TYPES[mainType].map(sub => (
      <div 
        key={sub.id} 
        className={`submenu-pill ${cardParams.Type === sub.id ? 'active' : ''}`} 
        onClick={() => updateParam('Type', sub.id)}
      >
        {sub.label}
      </div>
    ))
  }
</div>

      <div className="preview-placeholder pe-md-4" ref={placeholderRef} onClick={() => { if (!cardParams.Art?.File && !isMini) { onOpenModal('art'); } }}>
        <div className={`preview-layout no-swipe ${isMini ? 'is-mini no-swipe' : ''}`} ref={layoutRef} onClick={handleMiniClick}>
          
<CardCanvas 
             ref={canvasRef}
             cardParams={cardParams} 
             isArtLocked={isArtLocked || isMini} 
             zoomLevel={zoomLevel} 
             artUrl={cardParams.Art?.File || ''} 
             onZoomConstraints={handleZoomSetup}
             onArtTransform={handleArtTransform}
             onTextRendered={onTextRendered}
             showToast={showToast}
          />

          <div className="canvas-tools-panel">
            <button className="tool-btn upload-btn mb-2" onClick={(e) => { e.stopPropagation(); onOpenModal('art'); }} title="Upload Custom Art">
              <FaImage />
            </button>
            <button className="tool-btn-small" onClick={(e) => { e.stopPropagation(); setZoomLevel(z => { const nz = Math.min(zoomConstraints.max, z + 1); handleArtTransform({scale: nz/100}); return nz; }); }}><FaPlus /></button>
            
            <input 
              type="range" 
              className="vertical-slider my-2" 
              orient="vertical"
              min={zoomConstraints.min} 
              max={zoomConstraints.max}
              value={zoomLevel} 
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setZoomLevel(Number(e.target.value))} 
              onMouseUp={() => handleArtTransform({scale: zoomLevel/100})} // Save on interaction end
              onTouchEnd={() => handleArtTransform({scale: zoomLevel/100})}
            />

            <button className="tool-btn-small mb-2" onClick={(e) => { e.stopPropagation(); setZoomLevel(z => { const nz = Math.max(zoomConstraints.min, z - 1); handleArtTransform({scale: nz/100}); return nz; }); }}><FaMinus /></button>
            <button
              type="button"
              className={`tool-btn mt-1 ${showArtAdjustPanel ? 'unlocked' : 'locked'}`}
              onClick={(e) => {
                e.stopPropagation();
                setShowArtAdjustPanel((isOpen) => !isOpen);
              }}
              title="Adjust Image"
              aria-label="Adjust Image"
              aria-expanded={showArtAdjustPanel}
              aria-controls="mobile-art-adjustments"
            >
              <FaSliders />
            </button>
            <button 
              className={`tool-btn mt-1 ${isArtLocked ? 'locked' : 'unlocked'}`} 
              onClick={(e) => { e.stopPropagation(); setIsArtLocked(!isArtLocked); }}
              title={isArtLocked ? "Click to Pan Art" : "Click to Lock Art"}
            >
              {isArtLocked ? <FaArrowsAlt /> : <FaLockOpen />}
            </button>
          </div>
        </div>
      </div>

      {showArtAdjustPanel && (
        <div
          id="mobile-art-adjustments"
          className="mx-auto mt-3 submenu-pill"
          style={{
            width: 'min(100%, 430px)',
            padding: '12px',
            borderRadius: 12,
            border: '1px solid rgba(255, 255, 255, 0.18)',
            background: 'rgba(15, 18, 24, 0.96)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
            color: 'var(--text-main, #fff)'
          }}
        >
          <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
            <strong>Image Adjustments</strong>
            <div className="d-flex gap-2">
              <button
                type="button"
                className={`btn btn-sm ${cardParams.Art?.flipX ? 'btn-warning' : 'btn-outline-light'}`}
                onClick={() => updateArtAdjustment('flipX', !(cardParams.Art?.flipX ?? false))}
              >
                {cardParams.Art?.flipX ? 'Flipped' : 'Flip'}
              </button>
              <button type="button" className="btn btn-sm btn-outline-light" onClick={resetArtAdjustments}>
                Reset
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px 16px' }}>
            {ART_ADJUSTMENTS.map(({ key, label, min, max, defaultValue, suffix }) => {
              const value = cardParams.Art?.[key] ?? defaultValue;

              return (
                <label key={key} style={{ minWidth: 0, fontSize: 13 }}>
                  <span className="d-flex justify-content-between gap-2 mb-1">
                    <span>{label}</span>
                    <span>{value}{suffix}</span>
                  </span>
                  <input
                    type="range"
                    min={min}
                    max={max}
                    value={value}
                    onChange={(e) => updateArtAdjustment(key, Number(e.target.value))}
                    style={{ width: '100%', accentColor: 'var(--accent-gold, #fcd144)' }}
                  />
                </label>
              );
            })}
          </div>
        </div>
      )}

      <div className="d-flex justify-content-center flex-wrap gap-2 mt-3 copy-btn-wrapper">
        <button className="submenu-pill btn btn-sm rounded-pill px-3" onClick={() => onOpenModal('import')}>
          <GiCardPick /> Import
        </button>
        <button
          type="button"
          className="submenu-pill btn btn-sm rounded-pill px-3"
          onClick={handleCopyToClipboard}
          disabled={isCopyingToClipboard}
          aria-busy={isCopyingToClipboard}
        >
          <MdOutlineScreenshot /> {isCopyingToClipboard ? 'Copying…' : 'Clipboard'}
        </button>
        <button className="submenu-pill btn btn-sm rounded-pill px-3" onClick={() => canvasRef.current?.exportCard()}>
          <FaDownload /> Download
        </button>
        <input type="file" accept=".json" style={{display: 'none'}} ref={jsonInputRef} onChange={onImport} />
        <button className="submenu-pill btn btn-sm rounded-pill px-3" onClick={() => jsonInputRef.current.click()}>
          <BiSolidFileImport /> Import JSON
        </button>
        <button className="submenu-pill btn btn-sm rounded-pill px-3" onClick={onExport}>
          <BiSolidFileExport /> Export JSON
        </button>
      </div>

      {isCopyingToClipboard && typeof document !== 'undefined' && createPortal(
        <div
          role="status"
          aria-live="polite"
          aria-label="Copying card image to clipboard"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2147483647,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            background: 'rgba(4, 7, 11, 0.76)',
            backdropFilter: 'blur(1.5px)',
            WebkitBackdropFilter: 'blur(1.5px)',
            color: 'rgba(255, 255, 255, 0.94)',
            fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            touchAction: 'none',
            overscrollBehavior: 'contain'
          }}
        >
          <style>{`
            @keyframes mobileClipboardScan {
              0% { transform: translateY(-92px); opacity: 0; }
              18% { opacity: 0.42; }
              82% { opacity: 0.42; }
              100% { transform: translateY(92px); opacity: 0; }
            }

            @keyframes mobileClipboardPulse {
              0%, 100% { opacity: 0.62; transform: scale(1); }
              50% { opacity: 1; transform: scale(1.05); }
            }

            @media (prefers-reduced-motion: reduce) {
              .mobile-clipboard-scan,
              .mobile-clipboard-camera {
                animation: none !important;
              }
            }
          `}</style>

          <div
            style={{
              position: 'relative',
              width: 'min(74vw, 330px)',
              height: 'min(54vh, 440px)',
              minHeight: 280,
              maxHeight: 440,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden'
            }}
          >
            {[
              { top: 0, left: 0, borderTop: '3px solid rgba(255,255,255,0.82)', borderLeft: '3px solid rgba(255,255,255,0.82)' },
              { top: 0, right: 0, borderTop: '3px solid rgba(255,255,255,0.82)', borderRight: '3px solid rgba(255,255,255,0.82)' },
              { bottom: 0, left: 0, borderBottom: '3px solid rgba(255,255,255,0.82)', borderLeft: '3px solid rgba(255,255,255,0.82)' },
              { bottom: 0, right: 0, borderBottom: '3px solid rgba(255,255,255,0.82)', borderRight: '3px solid rgba(255,255,255,0.82)' }
            ].map((cornerStyle, index) => (
              <span
                key={index}
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  width: 42,
                  height: 42,
                  borderRadius: 3,
                  ...cornerStyle
                }}
              />
            ))}

            <span
              className="mobile-clipboard-scan"
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: '12%',
                right: '12%',
                top: '50%',
                height: 1,
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent)',
                boxShadow: '0 0 10px rgba(255,255,255,0.28)',
                animation: 'mobileClipboardScan 1.35s ease-in-out infinite'
              }}
            />

            <div
              className="mobile-clipboard-camera"
              aria-hidden="true"
              style={{
                width: 54,
                height: 54,
                marginBottom: 18,
                border: '1px solid rgba(255,255,255,0.34)',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 27,
                boxShadow: '0 8px 26px rgba(0,0,0,0.24)',
                animation: 'mobileClipboardPulse 1.2s ease-in-out infinite'
              }}
            >
              <MdOutlineScreenshot />
            </div>

            <strong style={{ fontSize: 17, letterSpacing: '0.01em' }}>
              Capturing card…
            </strong>
            <span
              style={{
                maxWidth: 240,
                marginTop: 7,
                color: 'rgba(255,255,255,0.68)',
                fontSize: 13,
                lineHeight: 1.4,
                textAlign: 'center'
              }}
            >
              Keep this screen open for just a moment.
            </span>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}